import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import { useUploadGuard } from '@/components/UploadGuard';
import { Upload as UploadIcon, FileSpreadsheet, X, CheckCircle2, Loader2, ArrowUpRight, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const expectedColumns = [
  'Job No', 'Job Date', 'Status', 'Service Head', 'Job Category', 'Job Type',
  'Supervisor', 'Technician', 'Customer Type', "Customer's Voice",
  'Vehicle In Date', 'Vehicle In Time', 'Expected Delivery Date',
  'Ramp In Date', 'Ramp In Time', 'Ramp Out Date', 'Ramp Out Time',
  'Party Name', 'Mobile No 1', 'Mobile No 2', 'Booking No', 'Email',
  'Brand', 'Model', 'Variant', 'Color', 'Chassis No', 'Engine No', 'Reg No', 'KMS',
];

export default function ServiceUpload({ user, onLogout }) {
  const navigate = useNavigate();
  const [file, setFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [drag, setDrag] = React.useState(false);
  const { ensureCompanyId, guardModal } = useUploadGuard(user);

  const onChooseFile = (selected) => {
    if (!selected) return;
    const isXl = /\.(xlsx|xls)$/i.test(selected.name);
    if (!isXl) { toast.error('Please upload an Excel file (.xlsx or .xls)'); return; }
    setFile(selected);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_company_id', targetCo);
      const { data } = await axios.post(`${API}/service/jobs/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(`Imported ${data.imported} job(s)`);
      setFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <ServiceLayout user={user} onLogout={onLogout} title="Upload Service Data" subtitle="Bulk import jobs from a DMS Excel export">
      {guardModal}      <div className="max-w-4xl mx-auto space-y-5" data-testid="service-upload-page">
        {/* Hero strip */}
        <div className="rounded-2xl p-5 text-white shadow-md shadow-indigo-500/20 relative overflow-hidden"
             style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #DB2777 100%)' }}>
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/15 blur-2xl" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
              <UploadIcon size={22} />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold tracking-tight">Bulk Job Card Import</h2>
              <p className="text-[12.5px] text-white/80">Job numbers will be re-assigned sequentially per Financial Year (no gaps).</p>
            </div>
          </div>
        </div>

        {/* Dropzone */}
        <div
          onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); onChooseFile(e.dataTransfer.files?.[0]); }}
          className={`rounded-2xl p-10 text-center bg-white/70 backdrop-blur border-2 border-dashed transition-all
            ${drag ? 'border-indigo-400 bg-indigo-50/40' : 'border-slate-200 hover:border-indigo-300'}`}
        >
          <div className="mx-auto w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3">
            <UploadIcon size={26} className="text-indigo-600" />
          </div>
          <p className="text-[15px] font-medium text-slate-800">{file ? file.name : 'Drop your Excel file here'}</p>
          <p className="text-[12.5px] text-slate-500 mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB · ready to import` : 'or click below to browse — .xlsx / .xls'}</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <input id="file-upload" type="file" accept=".xlsx,.xls" onChange={(e) => onChooseFile(e.target.files?.[0])} className="hidden" data-testid="file-input" />
            <label htmlFor="file-upload" className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-[13px] font-medium hover:bg-slate-50 cursor-pointer">Browse Files</label>
            {file && (
              <>
                <button onClick={() => setFile(null)} className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50 inline-flex items-center gap-1.5">
                  <X size={13} /> Remove
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="upload-btn"
                  className="px-4 py-2 rounded-xl text-white text-[13px] font-medium shadow-sm shadow-indigo-500/20 hover:shadow-md transition-all disabled:opacity-60 inline-flex items-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadIcon size={14} />}
                  Upload & Import
                </button>
              </>
            )}
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="rounded-2xl bg-white/75 backdrop-blur border border-emerald-200 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={18} className="text-emerald-600" />
              <h3 className="text-[15px] font-semibold text-emerald-800">Upload Complete</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Imported" value={result.imported || 0} accent="emerald" />
              <Stat label="Duplicates Skipped" value={result.duplicates || 0} accent="amber" Icon={AlertTriangle} />
              <Stat label="Errors" value={result.errors || 0} accent="rose" />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => navigate('/service/jobs')} className="px-4 py-2 rounded-xl text-white text-[13px] font-medium inline-flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}>
                View Jobs <ArrowUpRight size={13} />
              </button>
              <button onClick={() => navigate('/service/dashboard')} className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50">
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Expected columns */}
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileSpreadsheet size={16} className="text-slate-500" />
            <h3 className="text-[14px] font-semibold text-slate-800">Expected Columns</h3>
          </div>
          <p className="text-[12.5px] text-slate-500 mb-3">The file's first row should contain headers from the list below. Extra columns are ignored.</p>
          <div className="flex flex-wrap gap-1.5">
            {expectedColumns.map((c) => (
              <span key={c} className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11.5px] text-slate-700">{c}</span>
            ))}
          </div>
        </div>
      </div>
    </ServiceLayout>
  );
}

const Stat = ({ label, value, accent = 'indigo', Icon }) => {
  const tones = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    rose: 'text-rose-700 bg-rose-50 border-rose-200',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[accent] || tones.emerald}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} />}
        <span className="text-[11.5px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
};
